/**
 * HeatmapSharesList (Iter 31)
 *
 * Renders below the heat map's main content. Lists active + expired/revoked
 * share tokens for the workspace, with view counts + revoke buttons. The list
 * collapses to a single header row when there are no shares yet, so the page
 * doesn't feel cluttered for first-time users.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ShareNetworkIcon as ShareNetwork,
  TrashIcon as Trash,
  EyeIcon as Eye,
  ClockIcon as Clock,
  CopyIcon as Copy,
  CheckCircleIcon as CheckCircle,
  EnvelopeSimpleIcon as Envelope,
  BellIcon as Bell,
  BellSlashIcon as BellSlash,
} from "@phosphor-icons/react";
import { listHeatmapShares, revokeHeatmapShare, snoozeShare } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";
import NudgeModal from "./NudgeModal";

function shareUrl(token) {
  return `${window.location.origin}/share/heatmap/${token}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function StatusPill({ row }) {
  if (row.revoked) {
    return (
      <span
        className="ml-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border"
        style={{ background: "#f4f4f4", color: "#525252", borderColor: "#c6c6c6" }}
        data-testid="share-row-status-revoked"
      >
        Revoked
      </span>
    );
  }
  if (row.is_expired) {
    return (
      <span
        className="ml-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border"
        style={{ background: "#fff1f1", color: "#a2191f", borderColor: "#ffd7d9" }}
        data-testid="share-row-status-expired"
      >
        Expired
      </span>
    );
  }
  return (
    <span
      className="ml-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border inline-flex items-center gap-1"
      style={{ background: "#defbe6", color: "#0e6027", borderColor: "#a7f0ba" }}
      data-testid="share-row-status-active"
    >
      <CheckCircle size={9} weight="bold" /> Active
    </span>
  );
}

function ShareRow({ row, onRevoke, onNudge, onSnooze, canWrite }) {
  const recipient = row.recipient_email || row.recipient_label || (
    <span className="text-[var(--ml-text-secondary)] italic">(no recipient)</span>
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl(row.token));
      toast.success("Share URL copied.");
    } catch {
      toast.error("Couldn't access clipboard.");
    }
  };

  // Iter 34 — Nudge is only meaningful on active shares with a recipient_email.
  // Label-only shares can't be nudged (no addressable email).
  const canNudge = row.is_active && Boolean(row.recipient_email) && canWrite;
  // Iter 34.d — snooze management. Show the Bell-Slash button on active shares
  // (write-scope), but flip its semantic to "unsnooze" when already snoozed.
  const isSnoozed = Boolean(row.snoozed_until && new Date(row.snoozed_until) > new Date());
  const canManageSnooze = row.is_active && canWrite;
  const snoozeDaysLeft = isSnoozed
    ? Math.max(1, Math.ceil((new Date(row.snoozed_until) - new Date()) / 86_400_000))
    : 0;

  return (
    <tr
      className="border-b border-[var(--ml-border)] hover:bg-[var(--ml-surface)]/30"
      data-testid={`share-row-${row.token_suffix}`}
    >
      <td className="px-3 py-2"><StatusPill row={row} /></td>
      <td className="px-3 py-2 text-sm">
        {recipient}
        {row.recipient_email && row.recipient_label && (
          <span className="block text-xs text-[var(--ml-text-secondary)]">{row.recipient_label}</span>
        )}
        {isSnoozed && (
          <span
            className="ml-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border inline-flex items-center gap-1 mt-1"
            style={{ background: "#edf5ff", color: "#0043ce", borderColor: "#a6c8ff" }}
            data-testid={`share-row-snoozed-${row.token_suffix}`}
            title={`Snoozed until ${row.snoozed_until?.slice(0, 10)} — won't appear in chase candidates or bulk nudge.`}
          >
            <BellSlash size={9} weight="bold" /> Snoozed {snoozeDaysLeft}d
          </span>
        )}
      </td>
      <td className="px-3 py-2 ml-mono text-xs">
        …{row.token_suffix}
      </td>
      <td className="px-3 py-2 ml-mono text-xs text-right">
        <span className="inline-flex items-center gap-1">
          <Eye size={11} weight="bold" /> {row.view_count}
        </span>
      </td>
      <td className="px-3 py-2 ml-mono text-xs">
        {row.last_viewed_at ? (
          <span className="inline-flex items-center gap-1">
            {fmtDate(row.last_viewed_at)}
            {row.first_view_notified && (
              <span
                title={`First-view notification email sent to ${row.created_by_email || "creator"}${row.first_view_via ? ` (${row.first_view_via})` : ""}`}
                className="inline-flex items-center gap-0.5 text-[var(--ml-primary)]"
                data-testid={`share-row-notified-${row.token_suffix}`}
              >
                <Envelope size={10} weight="bold" />
              </span>
            )}
          </span>
        ) : "—"}
      </td>
      <td className="px-3 py-2 ml-mono text-xs">
        <span className="inline-flex items-center gap-1">
          <Clock size={11} weight="bold" /> {fmtDate(row.expires_at)}
        </span>
      </td>
      <td className="px-3 py-2">
        <div className="flex gap-1 justify-end">
          {row.is_active && (
            <>
              <button
                type="button"
                onClick={handleCopy}
                data-testid={`share-row-copy-${row.token_suffix}`}
                className="ml-btn-ghost p-1.5"
                title="Copy share URL"
              >
                <Copy size={12} weight="bold" />
              </button>
              {canNudge && (
                <button
                  type="button"
                  onClick={() => onNudge(row)}
                  data-testid={`share-row-nudge-${row.token_suffix}`}
                  className="ml-btn-ghost p-1.5 text-[var(--ml-primary)]"
                  title={
                    (row.nudges_sent ?? 0) > 0
                      ? `Nudge again (last sent: ${row.nudges_sent} time${row.nudges_sent === 1 ? "" : "s"})`
                      : "Send a reminder email to this recipient"
                  }
                >
                  <Bell size={12} weight="bold" />
                </button>
              )}
              {canManageSnooze && (
                <button
                  type="button"
                  onClick={() => onSnooze(row, isSnoozed ? 0 : 7)}
                  data-testid={`share-row-snooze-${row.token_suffix}`}
                  className="ml-btn-ghost p-1.5 text-[var(--ml-text-secondary)]"
                  title={
                    isSnoozed
                      ? `Unsnooze (currently silenced for ${snoozeDaysLeft} more day${snoozeDaysLeft === 1 ? "" : "s"})`
                      : "Snooze for 7 days (hide from chase candidates)"
                  }
                >
                  <BellSlash size={12} weight="bold" />
                </button>
              )}
              {canWrite && (
                <button
                  type="button"
                  onClick={() => onRevoke(row.token)}
                  data-testid={`share-row-revoke-${row.token_suffix}`}
                  className="ml-btn-ghost p-1.5 text-[#a2191f]"
                  title="Revoke this share link"
                >
                  <Trash size={12} weight="bold" />
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function HeatmapSharesList({ workspaceId, canWrite = true, refreshKey = 0 }) {
  const [shares, setShares] = useState(null);
  const [error, setError] = useState(null);
  // Iter 34 — track which share (if any) is currently being nudged.
  const [nudgeShare, setNudgeShare] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const list = await listHeatmapShares(workspaceId);
      setShares(list);
    } catch (e) {
      setError(formatApiErrorDetail(e) || "Failed to load shares");
    }
  }, [workspaceId]);

  useEffect(() => {
    refresh();
  }, [refresh, refreshKey]);

  const handleRevoke = async (token) => {
    if (!window.confirm("Revoke this share link? The URL will stop working immediately.")) return;
    try {
      await revokeHeatmapShare(workspaceId, token);
      toast.success("Share link revoked.");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to revoke");
    }
  };

  const handleNudgeClick = (row) => setNudgeShare(row);
  const handleNudgeSent = () => {
    // Refresh so the row's nudges_sent / last_nudge_at + cooldown state stays current.
    refresh();
  };

  // Iter 34.d — snooze / unsnooze handler. Days=0 unsnoozes; >0 snoozes.
  const handleSnooze = async (row, days) => {
    if (!row?.token) return;
    try {
      await snoozeShare(workspaceId, row.token, days);
      if (days === 0) {
        toast.success("Unsnoozed — share is back in chase candidates.");
      } else {
        toast.success(`Snoozed for ${days} day${days === 1 ? "" : "s"} — won't appear in chase candidates.`);
      }
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to snooze");
    }
  };

  if (error) {
    return <div className="ml-card p-3 text-sm text-[#a2191f]" data-testid="shares-list-error">{error}</div>;
  }

  if (!shares) {
    return (
      <div className="ml-card p-3 text-xs text-[var(--ml-text-secondary)]" data-testid="shares-list-loading">
        Loading active shares…
      </div>
    );
  }

  if (shares.length === 0) {
    return (
      <div className="ml-card p-4" data-testid="shares-list-empty">
        <div className="flex items-center gap-2 text-sm text-[var(--ml-text-secondary)]">
          <ShareNetwork size={14} weight="bold" />
          No share links yet. Click <strong>Share</strong> above to mint a public read-only URL.
        </div>
      </div>
    );
  }

  const active = shares.filter((s) => s.is_active);
  const inactive = shares.filter((s) => !s.is_active);

  return (
    <>
      <div className="ml-card overflow-x-auto" data-testid="shares-list">
        <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--ml-border)]">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5">
            <ShareNetwork size={11} weight="bold" /> Share links
            {active.length > 0 && (
              <span className="ml-1 inline-flex items-center" style={{ color: "#0e6027" }}>
                · {active.length} active
              </span>
            )}
            {inactive.length > 0 && (
              <span className="ml-1 text-[var(--ml-text-secondary)]">
                · {inactive.length} inactive
              </span>
            )}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/50">
            <tr>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Recipient</th>
              <th className="px-3 py-2 text-left">Token</th>
              <th className="px-3 py-2 text-right">Views</th>
              <th className="px-3 py-2 text-left">Last viewed</th>
              <th className="px-3 py-2 text-left">Expires</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {[...active, ...inactive].map((row) => (
              <ShareRow
                key={row.token}
                row={row}
                onRevoke={handleRevoke}
                onNudge={handleNudgeClick}
                onSnooze={handleSnooze}
                canWrite={canWrite}
              />
            ))}
          </tbody>
        </table>
      </div>
      <NudgeModal
        workspaceId={workspaceId}
        share={nudgeShare}
        open={Boolean(nudgeShare)}
        onClose={() => setNudgeShare(null)}
        onSent={handleNudgeSent}
      />
    </>
  );
}
